"use client"

import {useCallback, useEffect, useRef, useState, useTransition} from "react";
import {usePathname, useRouter} from "@/i18n/navigation";
import {localelessPath} from "@/lib/utils/i18n/localelessPath";
import {persistLocaleChoice} from "@/lib/utils/i18n/persistLocaleChoice";

// AC-883: switching the locale straight from Radix `onValueChange` tears the
// route down while the select is still closing. That unmount lands in the same
// commit as Radix's Presence / ItemText state machines — `send('ANIMATION_END')`
// on one side, `setItemTextNode(null)` from the detaching ref on the other —
// and they keep pushing setState at each other until React gives up with #185
// ("Maximum update depth exceeded"). With no error.tsx under [locale] that used
// to take the whole page down to the default Next error screen.
//
// So: remember the choice, close the popup, and navigate only once it is gone —
// two frames later (one to unmount the closing content, one to leave that
// commit) and inside a transition, so the route swap gets its own commit
// instead of joining the one that is closing the select.
export function useDeferredLocaleSwitch() {
    const router = useRouter();
    const intlPathname = usePathname();
    const [open, setOpen] = useState(false);
    const pendingLocale = useRef<string | null>(null);
    const [, startTransition] = useTransition();

    const onLocaleChange = useCallback((locale: string) => {
        pendingLocale.current = locale;
        setOpen(false);
    }, []);

    useEffect(() => {
        if (open || pendingLocale.current === null) return;

        const locale = pendingLocale.current;

        let innerFrame = 0;
        const outerFrame = requestAnimationFrame(() => {
            innerFrame = requestAnimationFrame(() => {
                // Cleared here rather than when scheduling: if this effect is torn
                // down before the frames fire, the choice survives and the next run
                // re-schedules it instead of dropping the switch silently.
                pendingLocale.current = null;
                // Pin the choice before navigating — next-intl may skip its own
                // cookie write (see persistLocaleChoice), and the cookie is what
                // the next reload of an unprefixed URL resolves from.
                persistLocaleChoice(locale);
                startTransition(() => {
                    // Build the target from the REAL current URL (window.location), not
                    // next-intl's usePathname() which goes stale after a client-side locale
                    // switch and caused double-locale 404s. See localelessPath().
                    const current = typeof window !== 'undefined' ? window.location.pathname : intlPathname;
                    router.replace(localelessPath(current), {locale, scroll: false});
                });
            });
        });

        return () => {
            cancelAnimationFrame(outerFrame);
            cancelAnimationFrame(innerFrame);
        };
    }, [open, intlPathname, router]);

    return {open, setOpen, onLocaleChange};
}
